import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup, signOut } from 'firebase/auth';

const AuthContext = createContext();
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

axios.defaults.timeout = 30000;

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(localStorage.getItem('fas_token') || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('fas_token');
    if (!savedToken) { setLoading(false); return; }

    axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
    axios.get(`${API}/auth/me`)
      .then(res => { setUser(res.data.user); setToken(savedToken); })
      .catch(() => {
        localStorage.removeItem('fas_token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Login — email/password, existing accounts ──────────────────
  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem('fas_token', t);
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    setToken(t);
    setUser(u);
    return u;
  };

  // ── Google — the only way NEW accounts are created ─────────────
  const loginWithGoogle = async () => {
    try {
      const result  = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken(false);

      const res = await axios.post(`${API}/auth/google`, { idToken });
      const { token: t, user: u } = res.data;

      localStorage.setItem('fas_token', t);
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      setToken(t);
      setUser(u);
      return u;
    } catch (err) {
      if (
        err.code === 'auth/popup-closed-by-user' ||
        err.code === 'auth/cancelled-popup-request' ||
        err.code === 'auth/internal-error'
      ) return;
      throw err;
    }
  };

  const logout = async () => {
    try { await signOut(auth); } catch {}
    localStorage.removeItem('fas_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const revealEmail = async (professorId) => {
    const res = await axios.post(`${API}/auth/reveal-email`, { professorId });
    setUser(prev => prev ? {
      ...prev,
      credits: res.data.creditsLeft,
      creditsUsedToday: res.data.dailyUsed ?? (prev.creditsUsedToday || 0) + 1,
    } : prev);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, loginWithGoogle,
      logout, revealEmail, setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}